import type { DateRangeEntry } from "../types";

import { AnalyticsDateRange, getChartIntervalBucketSizeMs } from "./dateBuckets";
import { isRecord } from "./isRecord";

import { AnalyticsChartInterval } from "@/queries/useRequestAnalytics";

const MINIMUM_DATE_RANGE_SELECTION_MS = 15_000;

interface ZoomRatio {
  start?: unknown;
  end?: unknown;
}

interface ZoomRange {
  start?: unknown;
  end?: unknown;
}

export interface ChartSelectionDateRange {
  from: Date;
  to: Date;
}

export const enforceMinimumDateRangeSelection = (
  range: ChartSelectionDateRange,
  bounds?: ChartSelectionDateRange,
): ChartSelectionDateRange => {
  const startTime = Math.min(range.from.getTime(), range.to.getTime());
  const endTime = Math.max(range.from.getTime(), range.to.getTime());
  if (endTime - startTime >= MINIMUM_DATE_RANGE_SELECTION_MS) {
    return {
      from: new Date(startTime),
      to: new Date(endTime),
    };
  }

  const boundsStart = bounds ? Math.min(bounds.from.getTime(), bounds.to.getTime()) : undefined;
  const boundsEnd = bounds ? Math.max(bounds.from.getTime(), bounds.to.getTime()) : undefined;
  if (
    boundsStart !== undefined &&
    boundsEnd !== undefined &&
    boundsEnd - boundsStart < MINIMUM_DATE_RANGE_SELECTION_MS
  ) {
    return {
      from: new Date(boundsStart),
      to: new Date(boundsEnd),
    };
  }

  let nextFrom = startTime;
  let nextTo = startTime + MINIMUM_DATE_RANGE_SELECTION_MS;
  if (boundsEnd !== undefined && nextTo > boundsEnd) {
    nextTo = boundsEnd;
    nextFrom = boundsEnd - MINIMUM_DATE_RANGE_SELECTION_MS;
  }
  if (boundsStart !== undefined && nextFrom < boundsStart) {
    nextFrom = boundsStart;
    nextTo = boundsStart + MINIMUM_DATE_RANGE_SELECTION_MS;
  }

  return {
    from: new Date(nextFrom),
    to: new Date(nextTo),
  };
};

const getZoomRatioX = (event: unknown): ZoomRatio | null => {
  if (!isRecord(event) || !isRecord(event.ratioX)) {
    return null;
  }

  return event.ratioX;
};

const getZoomRangeX = (event: unknown): ZoomRange | null => {
  if (!isRecord(event) || !isRecord(event.rangeX)) {
    return null;
  }

  return event.rangeX;
};

const toFiniteRatio = (value: unknown): number | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }

  return Math.min(1, Math.max(0, value));
};

const getIndexForRangeValue = (dateEntries: DateRangeEntry[], value: unknown): number | null => {
  if (value === undefined || value === null) {
    return null;
  }

  const stringValue = String(value);
  const exactIndex = dateEntries.findIndex(entry => entry.key === stringValue);
  if (exactIndex !== -1) {
    return exactIndex;
  }

  const timestamp = Number(stringValue);
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  const firstIndexAfterValue = dateEntries.findIndex(entry => entry.date.getTime() >= timestamp);
  return firstIndexAfterValue === -1 ? dateEntries.length - 1 : firstIndexAfterValue;
};

const getIndexRangeFromZoomRange = (
  dateEntries: DateRangeEntry[],
  rangeX: ZoomRange | null,
): [number, number] | null => {
  const startIndex = getIndexForRangeValue(dateEntries, rangeX?.start);
  const endIndex = getIndexForRangeValue(dateEntries, rangeX?.end);
  if (startIndex === null || endIndex === null) {
    return null;
  }

  return startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
};

const getIndexRangeFromZoomRatio = (
  dateEntries: DateRangeEntry[],
  ratioX: ZoomRatio | null,
): [number, number] | null => {
  const startRatio = toFiniteRatio(ratioX?.start);
  const endRatio = toFiniteRatio(ratioX?.end);
  if (startRatio === null || endRatio === null || startRatio === endRatio) {
    return null;
  }

  const [leftRatio, rightRatio] =
    startRatio < endRatio ? [startRatio, endRatio] : [endRatio, startRatio];
  const lastIndex = dateEntries.length - 1;
  const startIndex = Math.max(0, Math.min(lastIndex, Math.floor(leftRatio * lastIndex)));
  const endIndex = Math.max(startIndex, Math.min(lastIndex, Math.ceil(rightRatio * lastIndex)));

  return [startIndex, endIndex];
};

const buildRatioSelectedRange = (
  dateRange: AnalyticsDateRange,
  ratioX: ZoomRatio | null,
): AnalyticsDateRange | null => {
  const startRatio = toFiniteRatio(ratioX?.start);
  const endRatio = toFiniteRatio(ratioX?.end);
  if (startRatio === null || endRatio === null || startRatio === endRatio) {
    return null;
  }

  const [leftRatio, rightRatio] =
    startRatio < endRatio ? [startRatio, endRatio] : [endRatio, startRatio];
  const startTime = dateRange.from.getTime();
  const durationMs = dateRange.to.getTime() - startTime;

  return {
    from: new Date(startTime + durationMs * leftRatio),
    to: new Date(startTime + durationMs * rightRatio),
  };
};

export const buildBucketAlignedRange = (
  dateEntries: DateRangeEntry[],
  dateRange: AnalyticsDateRange,
  chartInterval: AnalyticsChartInterval,
  indexRange: [number, number],
) => {
  const [startIndex, endIndex] = indexRange;
  const bucketSizeMs = getChartIntervalBucketSizeMs(chartInterval);
  const selectedStart = dateEntries[startIndex].date.getTime();
  const selectedEnd = dateEntries[endIndex].date.getTime() + bucketSizeMs - 1;

  return {
    from: new Date(Math.max(dateRange.from.getTime(), selectedStart)),
    to: new Date(Math.min(dateRange.to.getTime(), selectedEnd)),
  };
};

export const createDateRangeSelectionHandler =
  (
    dateEntries: DateRangeEntry[],
    dateRange: ChartSelectionDateRange,
    chartInterval: AnalyticsChartInterval,
    onDateRangeSelect: (range: { from: Date; to: Date }) => void,
  ) =>
  (event: unknown) => {
    if (dateEntries.length === 0) {
      return;
    }

    const ratioRange = buildRatioSelectedRange(dateRange, getZoomRatioX(event));
    if (ratioRange) {
      onDateRangeSelect(enforceMinimumDateRangeSelection(ratioRange, dateRange));
      return;
    }

    const indexRange =
      getIndexRangeFromZoomRange(dateEntries, getZoomRangeX(event)) ??
      getIndexRangeFromZoomRatio(dateEntries, getZoomRatioX(event));
    if (!indexRange) {
      return;
    }

    onDateRangeSelect(
      enforceMinimumDateRangeSelection(
        buildBucketAlignedRange(dateEntries, dateRange, chartInterval, indexRange),
        dateRange,
      ),
    );
  };
